import {annotateHTML} from '@frontapp/html-highlights-transformer';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {iSolemnlySwearThisHtmlIsClean} from '../../../../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {MessageAnnotationModel} from '../../../../../../../libs/shared/core/src/models/api/annotationModel';
import {LoadedMessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {filterHighlightAnnotationsForMessageContent} from '../../../../../../../libs/shared/core/src/models/helpers/annotationHelpers';
import {TimelineVariationsEnum} from '../../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {markFullStorySensitive} from '../../../../../../helpers/fullstoryHelpers';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../../common/annotation/annotationsHighlightViewer';
import {AnnotationsHighlightViewerContainer} from '../../../../../common/annotation/annotationsHighlightViewerContainer';
import {buildAnnotationCloseTag, buildAnnotationTagOpen} from '../helpers/annotationTagsHelper';
import {MessageViewerBody} from '../messageViewerBody';

/*
 * Props.
 */

export interface MessageViewerTweetBodyProps
  extends AnnotationsHighlightViewerInheritableProps<MessageAnnotationModel> {
  message: LoadedMessageModel;
  timelineVariation: TimelineVariationsEnum;
}

/*
 * Style.
 */

const StyledTweetDiv = styled.div`
  ${textSelection()};

  padding: 12px 16px;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  line-height: 24px;

  a.tweet-url {
    font-weight: ${fontWeights.medium};
  }
`;

/*
 * Component.
 */

export const MessageViewerTweetBody: FC<MessageViewerTweetBodyProps> = (props) => {
  const {annotations, message} = props;
  const {body} = message;

  const filteredAnnotations = useMemo(
    () => filterHighlightAnnotationsForMessageContent(annotations),
    [annotations],
  );

  const bodyWithAnnotations = useMemo(() => {
    const annotatedBody = annotateHTML(body, filteredAnnotations, {
      onAnnotationOpen: (annotation) => buildAnnotationTagOpen(annotation),
      onAnnotationClose: (_annotation) => buildAnnotationCloseTag(),
    });

    return annotatedBody;
  }, [body, filteredAnnotations]);

  return (
    <AnnotationsHighlightViewerContainer
      annotations={filteredAnnotations}
      onCreateAnnotatedComment={props.onCreateAnnotatedComment}
      hasMessageAnnotations={props.hasMessageAnnotations}
      panelId={props.panelId}
      conversationId={props.message.conversationId}
    >
      <StyledTweetDiv className={markFullStorySensitive()}>
        <MessageViewerBody
          {...props}
          trustedHtml={iSolemnlySwearThisHtmlIsClean(bodyWithAnnotations)}
          messageId={props.message.id}
          attachments={props.message.attachments}
          isExternalContentHidden={false}
          onCreateAnnotatedComment={props.onCreateAnnotatedComment}
          timelineVariation={props.timelineVariation}
        />
      </StyledTweetDiv>
    </AnnotationsHighlightViewerContainer>
  );
};
